<template>
    <teleport to="body">
        <div
            v-if="active && currentStepData"
            role="dialog"
            aria-modal="true"
            :aria-label="`Product tour step ${currentStep + 1} of ${totalSteps}`"
            @keydown.escape="skip"
            @keydown.right.prevent="next"
            @keydown.left.prevent="back"
            @keydown.tab.prevent="trapFocus"
            tabindex="-1"
            ref="tourRoot"
        >
            <!-- Spotlight overlay -->
            <div
                v-if="spotlightStyle"
                class="fixed pointer-events-none z-[9998]"
                :style="overlayStyle"
            >
                <!-- Top -->
                <div
                    class="absolute inset-x-0 top-0 bg-black/50"
                    :style="{ height: spotlightStyle.top + 'px' }"
                />
                <!-- Bottom -->
                <div
                    class="absolute inset-x-0 bottom-0 bg-black/50"
                    :style="{
                        top: spotlightStyle.top + spotlightStyle.height + 'px',
                    }"
                />
                <!-- Left -->
                <div
                    class="absolute bg-black/50"
                    :style="{
                        top: spotlightStyle.top + 'px',
                        height: spotlightStyle.height + 'px',
                        left: 0,
                        width: spotlightStyle.left + 'px',
                    }"
                />
                <!-- Right -->
                <div
                    class="absolute bg-black/50"
                    :style="{
                        top: spotlightStyle.top + 'px',
                        height: spotlightStyle.height + 'px',
                        left: spotlightStyle.left + spotlightStyle.width + 'px',
                        right: 0,
                    }"
                />
            </div>
            <!-- Fallback full overlay when no target found -->
            <div
                v-else
                class="fixed inset-0 bg-black/50 z-[9998] pointer-events-none"
            />

            <!-- Click-blocker so user can't interact behind the popover -->
            <div class="fixed inset-0 z-[9998]" @click.self="skip" />

            <!-- Popover card -->
            <div
                ref="popover"
                class="fixed z-[9999] w-72 rounded-xl bg-white shadow-2xl ring-1 ring-black/10 focus:outline-none"
                :style="popoverStyle"
                data-testid="product-tour-popover"
            >
                <!-- Arrow -->
                <div
                    v-if="arrowStyle"
                    ref="arrowEl"
                    class="absolute h-2.5 w-2.5 rotate-45 bg-white ring-1 ring-black/10"
                    :style="arrowStyle"
                />

                <!-- Header -->
                <div
                    class="rounded-t-xl bg-sky-500 px-4 py-3 flex items-center justify-between"
                >
                    <span class="text-sm font-medium text-white">
                        {{ currentStepData.title }}
                    </span>
                    <span class="text-xs text-sky-200">
                        {{ currentStep + 1 }} / {{ totalSteps }}
                    </span>
                </div>

                <!-- Body -->
                <div class="px-4 py-3">
                    <p class="text-sm text-taupe-600 leading-relaxed">
                        {{ currentStepData.description }}
                    </p>
                </div>

                <!-- Footer -->
                <div class="px-4 pb-4 flex items-center justify-between">
                    <zen-button
                        type="button"
                        variant="link-muted"
                        data-testid="product-tour-skip"
                        @click="skip"
                    >
                        Skip tour
                    </zen-button>
                    <div class="flex items-center gap-2">
                        <zen-button
                            v-if="currentStep > 0"
                            type="button"
                            variant="secondary"
                            size="small"
                            data-testid="product-tour-back"
                            @click="back"
                        >
                            Back
                        </zen-button>
                        <zen-button
                            type="button"
                            variant="primary"
                            size="small"
                            data-testid="product-tour-next"
                            @click="next"
                        >
                            {{ currentStep < totalSteps - 1 ? 'Next' : 'Done' }}
                        </zen-button>
                    </div>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import {
    autoUpdate,
    computePosition,
    flip,
    offset,
    shift,
    arrow,
} from '@floating-ui/dom'
import { TOUR_STEPS } from '@/composables/useProductTour'

const props = defineProps({
    active: {
        type: Boolean,
        required: true,
    },
    currentStep: {
        type: Number,
        required: true,
    },
})

const emit = defineEmits(['next', 'back', 'skip'])

const tourRoot = ref(null)
const popover = ref(null)
const arrowEl = ref(null)

let previouslyFocused = null

const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ')

function getFocusableEls() {
    return Array.from(popover.value.querySelectorAll(FOCUSABLE))
}

function trapFocus(event) {
    const els = getFocusableEls()
    if (!els.length) return
    const first = els[0]
    const last = els[els.length - 1]
    if (event.shiftKey) {
        if (document.activeElement === first) {
            last.focus()
        }
    } else {
        if (document.activeElement === last) {
            first.focus()
        }
    }
}

const popoverStyle = ref({
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
})
const arrowStyle = ref(null)
const spotlightStyle = ref(null)
const overlayStyle = ref({ position: 'fixed', inset: 0 })

const totalSteps = TOUR_STEPS.length
const currentStepData = computed(() => TOUR_STEPS[props.currentStep] ?? null)

function next() {
    emit('next')
}
function back() {
    emit('back')
}
function skip() {
    emit('skip')
}

const oppositeSideMap = new Map([
    ['top', 'bottom'],
    ['bottom', 'top'],
    ['left', 'right'],
    ['right', 'left'],
])

// Sync update function passed to autoUpdate — must be synchronous so it
// integrates cleanly with floating-ui's scheduler. The async computePosition
// call is fire-and-forget; layout thrash is prevented by floating-ui itself.
function updatePosition(target) {
    const rect = target.getBoundingClientRect()
    const pad = 4
    spotlightStyle.value = {
        top: rect.top - pad,
        left: rect.left - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
    }

    void computePosition(target, popover.value, {
        placement: currentStepData.value.placement ?? 'bottom',
        middleware: [
            offset(12),
            flip(),
            shift({ padding: 8 }),
            arrow({ element: arrowEl.value }),
        ],
    }).then(({ x, y, placement, middlewareData }) => {
        popoverStyle.value = {
            top: `${y}px`,
            left: `${x}px`,
            transform: 'none',
        }

        const { x: ax, y: ay } = middlewareData.arrow ?? {}
        const oppositeSide = oppositeSideMap.get(placement.split('-')[0])
        arrowStyle.value = {
            left: ax != null ? `${ax}px` : '',
            top: ay != null ? `${ay}px` : '',
            [oppositeSide]: '-5px',
        }
    })
}

let stopAutoUpdate = () => {}

function startPositioning() {
    stopAutoUpdate()

    const target = document.querySelector(
        `[data-tour="${currentStepData.value.target}"]`
    )

    if (!target) {
        spotlightStyle.value = null
        popoverStyle.value = {
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
        }
        arrowStyle.value = null
        return
    }

    stopAutoUpdate = autoUpdate(target, popover.value, () => {
        updatePosition(target)
    })
}

function stopPositioning() {
    stopAutoUpdate()
    stopAutoUpdate = () => {}
    spotlightStyle.value = null
}

watch(
    () => props.active,
    async (isActive) => {
        if (isActive) {
            previouslyFocused = document.activeElement
            await nextTick()
            tourRoot.value?.focus()
        } else {
            previouslyFocused?.focus()
            previouslyFocused = null
        }
    }
)

watch(
    () => [props.active, props.currentStep],
    async ([isActive]) => {
        if (!isActive) {
            stopPositioning()
            return
        }
        await nextTick()
        startPositioning()
    },
    { immediate: true }
)

onUnmounted(stopPositioning)
</script>
